import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import Layout from '@/components/layout/Layout';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import UsersList from '@/pages/users/UsersList';
import UserForm from '@/pages/users/UserForm';
import VenuesList from '@/pages/venues/VenuesList';
import VenueForm from '@/pages/venues/VenueForm';
import SubscriptionsList from '@/pages/subscriptions/SubscriptionsList';
import InvoicesList from '@/pages/invoices/InvoicesList';
import BookingTypesList from '@/pages/booking-types/BookingTypesList';
import BookingConfig from '@/pages/venues/booking-config/BookingConfig';
import AreasList from '@/pages/venues/booking-config/AreasList';
import AreaForm from '@/pages/venues/booking-config/AreaForm';
import TablesConfig from '@/pages/venues/booking-config/TablesConfig';
import SessionsList from '@/pages/venues/booking-config/SessionsList';
import SessionForm from '@/pages/venues/booking-config/SessionForm';
import SessionHoursForm from '@/pages/venues/booking-config/SessionHoursForm';
import FormsList from '@/pages/venues/forms/FormsList';
import FormBuilder from '@/pages/venues/forms/FormBuilder';
import FormSubmissions from '@/pages/venues/forms/FormSubmissions';
import FormPreview from '@/pages/venues/forms/FormPreview';
import CinemaConfig from '@/pages/venues/cinema/CinemaConfig';
import SeatTypesList from '@/pages/venues/cinema/SeatTypesList';
import ScreensList from '@/pages/venues/cinema/ScreensList';
import ScreenSections from '@/pages/venues/cinema/ScreenSections';
import SeatLayoutDesigner from '@/pages/venues/cinema/SeatLayoutDesigner';
import FilmsList from '@/pages/venues/cinema/FilmsList';
import FilmForm from '@/pages/venues/cinema/FilmForm';
import ShowingsList from '@/pages/venues/cinema/ShowingsList';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Allow system_admin, venue_admin, and group_admin
  const allowedRoles = ['system_admin', 'venue_admin', 'group_admin'];
  if (!user?.role || !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  const allowedRoles = ['system_admin', 'venue_admin', 'group_admin'];
  if (isAuthenticated && user?.role && allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Auth callback for cross-domain login from booking.appyfeed.com */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="users" element={<UsersList />} />
        <Route path="users/new" element={<UserForm />} />
        <Route path="users/:id/edit" element={<UserForm />} />
        <Route path="venues" element={<VenuesList />} />
        <Route path="venues/new" element={<VenueForm />} />
        <Route path="venues/:id/edit" element={<VenueForm />} />
        <Route path="subscriptions" element={<SubscriptionsList />} />
        <Route path="invoices" element={<InvoicesList />} />
        <Route path="booking-types" element={<BookingTypesList />} />

        {/* Booking Configuration Routes */}
        <Route path="venues/:venueId/booking-config" element={<BookingConfig />} />
        <Route path="venues/:venueId/booking-config/areas" element={<AreasList />} />
        <Route path="venues/:venueId/booking-config/areas/new" element={<AreaForm />} />
        <Route path="venues/:venueId/booking-config/areas/:areaId/edit" element={<AreaForm />} />
        <Route path="venues/:venueId/booking-config/areas/:areaId/tables" element={<TablesConfig />} />
        <Route path="venues/:venueId/booking-config/areas/:areaId/sessions" element={<SessionsList />} />
        <Route path="venues/:venueId/booking-config/areas/:areaId/sessions/new" element={<SessionForm />} />
        <Route path="venues/:venueId/booking-config/areas/:areaId/sessions/:sessionId" element={<SessionForm />} />
        <Route path="venues/:venueId/booking-config/areas/:areaId/sessions/:sessionId/hours" element={<SessionHoursForm />} />

        {/* General Request Forms */}
        <Route path="venues/:venueId/forms" element={<FormsList />} />
        <Route path="venues/:venueId/forms/new" element={<FormBuilder />} />
        <Route path="venues/:venueId/forms/:formId" element={<FormBuilder />} />
        <Route path="venues/:venueId/forms/:formId/preview" element={<FormPreview />} />
        <Route path="venues/:venueId/forms/:formId/submissions" element={<FormSubmissions />} />

        {/* Cinema Routes */}
        <Route path="venues/:venueId/cinema" element={<CinemaConfig />} />
        <Route path="venues/:venueId/cinema/seat-types" element={<SeatTypesList />} />
        <Route path="venues/:venueId/cinema/screens" element={<ScreensList />} />
        <Route path="venues/:venueId/cinema/screens/:screenId/sections" element={<ScreenSections />} />
        <Route path="venues/:venueId/cinema/screens/:screenId/layout" element={<SeatLayoutDesigner />} />
        <Route path="venues/:venueId/cinema/films" element={<FilmsList />} />
        <Route path="venues/:venueId/cinema/films/new" element={<FilmForm />} />
        <Route path="venues/:venueId/cinema/films/:filmId" element={<FilmForm />} />
        <Route path="venues/:venueId/cinema/films/:filmId/showings" element={<ShowingsList />} />
        <Route path="venues/:venueId/cinema/showings" element={<ShowingsList />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
